'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { useResponsesCardStyles } from './useResponsesCardStyles';
import DonutChart from '@ui/components/DonutChart/DonutChart';
import BarChart from '@ui/components/BarChart';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@application/store/useStore';
import { getEventAnalytics, getEventResponsesAnalytics } from '@application/features/eventAnalytics/event-analytics-thunk';
import { clearAnalytics } from '@application/features/eventAnalytics/event-analytics-slice';
import { getFamilyFestDashboardAnalytics, getFamilyFestResponsesAnalytics } from '@application/features/familyFest/familyFestAnalyticsThunk';
import { clearFamilyFestAnalytics } from '@application/features/familyFest/familyFestAnalyticsSlice';
const ResponsesCard = ({ eventId: selectedEventId, isFamilyFest = false }) => {
    const { t } = useTranslation();
    const styles = useResponsesCardStyles();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { analytics: regularAnalytics } = useSelector((state) => state.eventAnalytics);
    const { analytics: familyFestAnalytics } = useSelector((state) => state.familyFestAnalytics);
    const analytics = isFamilyFest ? familyFestAnalytics : regularAnalytics;
    const [lastFetchedKey, setLastFetchedKey] = useState(null);
    useEffect(() => {
        if (!selectedEventId) {
            setLastFetchedKey(null);
            return;
        }
        const fetchKey = `${selectedEventId}-${isFamilyFest ? 'family-fest' : 'regular'}`;
        // Only make API calls if this is a different eventId than what we last fetched
        if (lastFetchedKey === fetchKey) {
            return;
        }
        setLastFetchedKey(fetchKey);
        dispatch(clearAnalytics());
        dispatch(clearFamilyFestAnalytics());
        if (isFamilyFest) {
            dispatch(getFamilyFestDashboardAnalytics(selectedEventId)).then(() => {
                dispatch(getFamilyFestResponsesAnalytics(selectedEventId));
            });
        }
        else {
            dispatch(getEventAnalytics(selectedEventId)).then(() => {
                dispatch(getEventResponsesAnalytics(selectedEventId));
            });
        }
        return () => {
            dispatch(clearAnalytics());
            dispatch(clearFamilyFestAnalytics());
        };
    }, [selectedEventId, dispatch, lastFetchedKey, isFamilyFest]);
    const hasData = analytics && analytics.length > 0;
    return (_jsxs(Box, { children: [_jsx(Box, { children: _jsx(Typography, { variant: 'h6', color: 'textSecondary', children: t('events.responseOverview') }) }), hasData ? (_jsx(Box, { sx: styles.carouselWrapper, children: _jsx(Grid, { container: true, spacing: 2, children: analytics.map((field, fieldIndex) => {
                        const options = field.optionSelected ?? [];
                        const nonZeroOptions = options.filter((opt) => opt.value > 0);
                        const labels = nonZeroOptions.map((opt) => opt.key);
                        const values = nonZeroOptions.map((opt) => opt.value);
                        const barChartData = {
                            labels: [field.fieldLabel],
                            datasets: nonZeroOptions.map((opt, idx) => ({
                                label: opt.key,
                                data: [opt.value],
                                backgroundColor: [
                                    theme.palette.primary.light,
                                    theme.palette.success.main,
                                    theme.palette.info.light,
                                    theme.palette.warning.light,
                                    theme.palette.error.light
                                ][idx % 5],
                                borderColor: [
                                    theme.palette.primary.dark,
                                    theme.palette.success.dark,
                                    theme.palette.info.dark,
                                    theme.palette.warning.dark,
                                    theme.palette.error.dark
                                ][idx % 5]
                            }))
                        };
                        const chartData = {
                            labels,
                            values,
                            colors: labels.map((_, idx) => [
                                theme.palette.primary.light,
                                theme.palette.success.main,
                                theme.palette.info.light,
                                theme.palette.warning.light,
                                theme.palette.error.light
                            ][idx % 5])
                        };
                        return (_jsxs(Grid, { size: {
                                xs: 12,
                                sm: 6,
                                md: 6
                            }, sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }, children: [_jsx(Box, { sx: { height: 150, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: options.every((opt) => opt.value === 0) ? (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }, children: _jsx(Typography, { variant: 'body2', color: theme.palette.text.primary, children: t('events.noData') }) })) : (_jsx(Box, { sx: { height: '100%', width: '100%' }, children: !isFamilyFest && (options.length > 3 || field.fieldType === 9) ? (_jsx(BarChart, { showLegend: true, fillParent: true, data: barChartData, height: 150 })) : (_jsx(DonutChart, { fillParent: true, data: chartData, ringThickness: '85%', height: 150 })) })) }), _jsx(Typography, { textAlign: 'center', variant: 'caption', color: 'textSecondary', sx: { mt: 1, minHeight: '20px' }, children: field.fieldLabel })] }, fieldIndex));
                    }) }) })) : (_jsx(Box, { sx: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                    width: '100%'
                }, children: _jsx(Typography, { variant: 'body2', color: theme.palette.text.primary, children: t('events.noResponses') }) }))] }));
};
export default ResponsesCard;
